import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Breadcrumb from '../../components/UI/Breadcrumb/Breadcrumb';
import { useAvatars } from '../../hooks/useApi';
import AvatarUpload from './components/AvatarUpload';
import LoadingSpinner from '../../components/UI/LoadingSpinner/LoadingSpinner';
import { Construction, Copy, Check } from 'lucide-react';
import '../../styles/index.css';
import './MyAvatars.css';

const MyAvatars = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const initialTab = location.state?.tab || 'mine';
  const [activeTab, setActiveTab] = useState(initialTab); // mine | public | upload
  const [query, setQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [copiedKey, setCopiedKey] = useState(null);
  const copyTimerRef = useRef(null);

  const breadcrumbItems = [
    { label: '首页', path: '/dashboard' },
    { label: '我的数字人', path: null }
  ];

  // Use API hooks for different tabs
  const myAvatarsData = useAvatars('my', currentPage, query);
  const publicAvatarsData = useAvatars('public', currentPage, query);

  // Get current data based on active tab
  const getCurrentData = () => {
    return activeTab === 'mine' ? myAvatarsData : publicAvatarsData;
  };

  const { avatars, loading, error, totalPages, total, deleteAvatar } = getCurrentData();

  // Client-side filter as fallback (in case backend ignores search param)
  const filteredAvatars = useMemo(() => {
    if (!query) return avatars;
    const q = query.toLowerCase();
    return avatars.filter(a => a.name.toLowerCase().includes(q));
  }, [avatars, query]);

  useEffect(() => {
    if (totalPages === 0 && currentPage !== 1) {
      setCurrentPage(1);
    } else if (totalPages > 0 && currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [totalPages, currentPage]);

  const handleTab = (t) => {
    setActiveTab(t);
    setCurrentPage(1); // Reset to first page when switching tabs
  };

  const handleSearch = useCallback((e) => {
    setQuery(e.target.value);
    setCurrentPage(1); // Reset to first page when searching
  }, []);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleSelectAvatar = (avatar) => {
    localStorage.setItem('selectedAvatar', JSON.stringify(avatar));
    const returnTo = location.state?.from;
    const fromTab = location.state?.fromTab;
    if (returnTo) {
      navigate(returnTo, { replace: true, state: fromTab ? { tab: fromTab } : undefined });
    } else {
      navigate('/generate-video', { replace: true });
    }
  };

  const handleDeleteAvatar = async (avatarId) => {
    if (!avatarId) {
      alert('删除失败：未找到数字人标识');
      return;
    }

    if (window.confirm('确定要删除这个数字人吗？')) {
      try {
        await deleteAvatar(avatarId);
      } catch (error) {
        alert('删除失败：' + error.message);
      }
    }
  };

  const handleCopyKey = useCallback(async (key) => {
    if (!key) return;
    try {
      await navigator.clipboard.writeText(key);
      if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
      setCopiedKey(key);
      copyTimerRef.current = setTimeout(() => setCopiedKey(null), 2000);
    } catch {
      // ignore
    }
  }, []);

  const highlightText = (text, keyword) => {
    if (!keyword || !text) return text;
    const idx = text.toLowerCase().indexOf(keyword.toLowerCase());
    if (idx === -1) return text;
    return (
      <>
        {text.slice(0, idx)}
        <mark className="avatar-name-highlight">{text.slice(idx, idx + keyword.length)}</mark>
        {text.slice(idx + keyword.length)}
      </>
    );
  };

  const handleUploadSuccess = () => {
    // Switch back to "mine" tab and refresh
    setActiveTab('mine');
    myAvatarsData.refetch();
  };

  return (
    <div className="my-avatars-page">
        <Breadcrumb items={breadcrumbItems} />

        {/* Page Title */}
        <div className="page-header">
          <h1 className="page-title">
            {activeTab === 'upload' ? '创建数字人' : '我的数字人'}
          </h1>
          {activeTab !== 'upload' && (
            <button className="create-btn" onClick={() => handleTab('upload')}>
              + 创建数字人
            </button>
          )}
        </div>

        {/* Tabs */}
        <div className="ma-tabs">
          <button
            className={`ma-tab${activeTab === 'mine' ? ' active' : ''}`}
            onClick={() => handleTab('mine')}
          >
            我创建的
          </button>
          <button
            className={`ma-tab${activeTab === 'public' ? ' active' : ''}`}
            onClick={() => handleTab('public')}
          >
            公共库
          </button>
          <button
            className={`ma-tab${activeTab === 'upload' ? ' active' : ''}`}
            onClick={() => handleTab('upload')}
          >
            创建数字人
          </button>
        </div>

        {/* Toolbar — always rendered to keep consistent spacing across tabs */}
        <div className="toolbar">
          {activeTab !== 'upload' && (
            <>
              <input
                className="search-box"
                placeholder={activeTab === 'mine' ? '搜索我创建的数字人...' : '搜索公共数字人...'}
                value={query}
                onChange={handleSearch}
              />
              {activeTab === 'mine' && total > 0 && <span className="total-count">共 {total} 个数字人</span>}
            </>
          )}
        </div>

        {/* Content */}
        {activeTab === 'upload' ? (
          <AvatarUpload onSuccess={handleUploadSuccess} />
        ) : activeTab === 'public' ? (
          <div className="wip-state">
            <div className="wip-icon"><Construction size={28} /></div>
            <p className="wip-title">公共库正在建设中</p>
            <p className="wip-desc">我们正在精心整理公共数字人资源，敬请期待，即将上线</p>
          </div>
        ) : (
          <>
            {loading ? (
              <div className="flex justify-center items-center py-16">
                <LoadingSpinner />
              </div>
            ) : error ? (
              <div className="alert alert-error">
                <p>加载失败: {error}</p>
                <button className="btn btn-secondary btn-sm mt-2" onClick={() => getCurrentData().refetch()}>
                  重试
                </button>
              </div>
            ) : filteredAvatars.length === 0 ? (
              <div className="upload-hint">
                {query ? '没有找到匹配的数字人' : '点击"创建数字人"开始上传您的第一个数字人'}
              </div>
            ) : (
              <>
                <div className="cards-grid">
                  {filteredAvatars.map((avatar) => (
                    <div key={avatar.id} className="avatar-card">
                      <div className="avatar-thumb">
                        {avatar.thumbnail && (
                          <img
                            src={avatar.thumbnail}
                            alt={avatar.name}
                          />
                        )}
                      </div>
                      <div className="avatar-name">{highlightText(avatar.name, query)}</div>
                      <button
                        className={`avatar-key-copy${copiedKey === avatar.key ? ' copied' : ''}`}
                        onClick={(e) => { e.stopPropagation(); handleCopyKey(avatar.key); }}
                        title="点击复制人物VK"
                      >
                        <span className="avatar-key-text">{avatar.key?.substring(0, 16)}...</span>
                        {copiedKey === avatar.key
                          ? <Check size={12} />
                          : <Copy size={12} />
                        }
                        {copiedKey === avatar.key && (
                          <span className="avatar-key-tooltip">人物VK已经复制到剪贴板</span>
                        )}
                      </button>
                      <div className="avatar-card-actions">
                        <button
                          className="select-btn"
                          onClick={() => handleSelectAvatar(avatar)}
                        >
                          选择
                        </button>
                        {activeTab === 'mine' && (
                          <button
                            className="delete-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteAvatar(avatar.key);
                            }}
                          >
                            删除
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="pagination">
                    <button
                      className="page-btn"
                      disabled={currentPage === 1}
                      onClick={() => handlePageChange(currentPage - 1)}
                    >
                      ←
                    </button>

                    {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
                      let pageNum;
                      if (totalPages <= 7) {
                        pageNum = i + 1;
                      } else if (currentPage <= 4) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 3) {
                        pageNum = totalPages - 6 + i;
                      } else {
                        pageNum = currentPage - 3 + i;
                      }

                      return (
                        <button
                          key={pageNum}
                          className={`page-num ${currentPage === pageNum ? 'active' : ''}`}
                          onClick={() => handlePageChange(pageNum)}
                        >
                          {pageNum}
                        </button>
                      );
                    })}

                    <button
                      className="page-btn"
                      disabled={currentPage === totalPages}
                      onClick={() => handlePageChange(currentPage + 1)}
                    >
                      →
                    </button>
                  </div>
                )}
              </>
            )}
          </>
        )}
    </div>
  );
};

export default MyAvatars;